import {calculateErrorMessage} from 'common/helpers'
import {Ticket} from 'lucide-react'
import React, {useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'

import {useChatbotResolvedTheme} from '../../hooks/useChatbotResolvedTheme'
import {AssistantCapability} from '../../modes/assistant/assistantCapabilities'
import {
  AssistantClarification,
  AssistantClarificationOption,
  AssistantPresentation,
  AssistantStep,
  AssistantSuggestion,
} from '../../modes/assistant/assistantResponseContract'
import {AssistantSource} from '../../modes/assistant/assistantTypes'
import AttendanceMessageBody from '../../modes/attendance/AttendanceMessageBody'
import {AttendanceInputSuggestion} from '../../modes/attendance/attendanceInputSuggestions'
import {AttendanceV2Payload} from '../../modes/attendance/attendanceV2Types'
import {
  AttendanceChatSendExtras,
  AttendanceStudentReport,
} from '../../modes/attendance/attendanceWizardTypes'
import {AttendanceParsedContent} from '../../modes/attendance/parseAttendanceAnswer'
import AssistantClarificationChips from '../chat/AssistantClarificationChips'
import AssistantSuggestionChips from '../chat/AssistantSuggestionChips'
import AttendanceInputSuggestionPills from '../chat/AttendanceInputSuggestionPills'
import OptionChips from '../chat/OptionChips'
import AttendanceBotAvatar from '../ui/AttendanceBotAvatar'
import AssistantStepsView from './AssistantStepsView'
import DirectoryProfileCard from './DirectoryProfileCard'
import {parseDirectoryProfile} from './directoryProfile'
import MarkdownTableCard from './MarkdownTableCard'
import {MarkdownTableSegment, splitMarkdownTables} from './markdownTables'
import {extractLinks} from './messageLinks'

interface BotMessageProps {
  message: {
    id?: string
    timestamp: any
    text: string | string[]
    content?: AttendanceParsedContent
    v2Payload?: AttendanceV2Payload | null
    rawApiAnswer?: string
    optionChips?: Array<{label: string; value: string}>
    attendanceContext?: Record<string, unknown> | null
    /** Board that produced this answer (unified assistant); unset for legacy/general bots. */
    source?: AssistantSource
    /** Set on answers to a support ask, so the ticket action only follows those. */
    offerTicket?: boolean
    /** What the backend said this turn is — decides what renders under the prose. */
    presentation?: AssistantPresentation | null
    /** The question asked back, with its options (`clarify` / `menu`). */
    clarification?: AssistantClarification | null
    /** Widening offers on a no-data answer (`meta.suggestions`). */
    suggestions?: AssistantSuggestion[]
    /** Walkthrough steps of a `steps` turn (`data.steps`). */
    steps?: AssistantStep[]
    /** Backend capability that answered (`fee.data`, `attendance.data`); names table exports. */
    capability?: string | null
  }
  theme?: 'light' | 'dark'
  onOptionSelect?: (value: string, extras?: AttendanceChatSendExtras) => void
  onChangeAttendanceDates?: (reports: AttendanceStudentReport[]) => void
  welcomeSuggestions?: AttendanceInputSuggestion[]
  onWelcomeSuggestionSelect?: (text: string) => void
  welcomeSuggestionsDisabled?: boolean
  /** Handoff action shown under every answer flagged `offerTicket` (the answer talks about support). */
  onCreateTicket?: () => void
  /** A clarification option was picked — see `AssistantClarificationChips`. */
  onClarificationSelect?: (option: AssistantClarificationOption) => void
  /** A widening suggestion was picked; its `query` is what gets asked. */
  onSuggestionSelect?: (suggestion: AssistantSuggestion) => void
  /** Chips and actions are inert while a turn is in flight. */
  chipsDisabled?: boolean
  /** What the assistant can do, shown on the welcome message only. */
  capabilities?: AssistantCapability[]
  onCapabilitySelect?: (capability: AssistantCapability) => void
}

interface ParsedMessage {
  html: string
}

/** Inline lucide arrow-right, used in place of the → glyph in bot answers. */
// The hidden "›" is what copy/paste and screen readers get — without it a copied
// breadcrumb read "SidebarOtherSystem Reports", because the SVG carries no text.
export const ARROW_ICON_HTML =
  '<svg class="chatbot-arrow-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg><span class="visually-hidden">›</span>'

/**
 * Draw a directory record ("show student with GR number …") as a profile card. Set to false to
 * fall back to the backend's one-sentence rendering.
 */
const DIRECTORY_PROFILE_CARD = true

/** Swap every → for the inline arrow, keeping a space on each side. */
export const replaceArrows = (html: string): string =>
  html.replace(/\s*→\s*/g, ` ${ARROW_ICON_HTML} `)

const BotMessage: React.FC<BotMessageProps> = ({
  message,
  onOptionSelect,
  welcomeSuggestions,
  onWelcomeSuggestionSelect,
  welcomeSuggestionsDisabled = false,
  onCreateTicket,
  onClarificationSelect,
  onSuggestionSelect,
  chipsDisabled = false,
}) => {
  const intl = useIntl()
  const theme = useChatbotResolvedTheme()
  const removeCitations = (text: string): string => {
    return text.replace(/\s*\*\([Ss]ource[^)]*\)\*/g, '')
  }

  const escapeHtml = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  const markdownToHtml = (text: string): string => {
    // Links come out first (and go back at the end), so the markdown rules below can't break a URL
    // apart; a long bare URL comes back as an Open link button.
    const links = extractLinks(
      escapeHtml(text || ''),
      intl.formatMessage({id: 'CHATBOT.MESSAGE.OPEN_LINK'})
    )
    let html = links.text

    // Restore bold tags that arrived from the backend (they were escaped above)
    html = html.replace(/&lt;(strong|b)&gt;(.*?)&lt;\/(strong|b)&gt;/gi, '<strong>$2</strong>')

    // Normalize every arrow notation the backend uses (→ entity references and
    // ASCII ->/=>, both escaped above) to the plain glyph; it is swapped for an
    // inline SVG icon at the end of the pipeline.
    html = html.replace(/&amp;(rarr|rightarrow|#8594|#x2192);/gi, '→')
    html = html.replace(/[-=]&gt;/g, '→')

    html = html.replace(/^\s*(---|\*\*\*|___)\s*$/gm, '<hr/>')
    html = html.replace(/```([\s\S]*?)```/gm, (m, code) => `<pre><code>${code.trim()}</code></pre>`)
    html = html.replace(/^>\s?(.*)$/gm, '<blockquote>$1</blockquote>')
    html = html.replace(/^\s*######\s+(.*?)\s*$/gm, '<h6>$1</h6>')
    html = html.replace(/^\s*#####\s+(.*?)\s*$/gm, '<h5>$1</h5>')
    html = html.replace(/^\s*####\s+(.*?)\s*$/gm, '<h4>$1</h4>')
    html = html.replace(/^\s*###\s+(.*?)\s*$/gm, '<h3>$1</h3>')
    html = html.replace(/^\s*##\s+(.*?)\s*$/gm, '<h2>$1</h2>')
    html = html.replace(/^\s*#\s+(.*?)\s*$/gm, '<h1>$1</h1>')
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
    html = html.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>')
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Only a whole `_word_` is emphasis — inside a word the underscores belong to it
    // (usernames such as adam_mohamed_saad1 came out as adam<em>mohamed</em>saad1).
    html = html.replace(/(^|[^\w])_([^_\n]+)_(?!\w)/g, '$1<em>$2</em>')
    html = html.replace(/\\([*_`])/g, '$1')
    html = parseLists(html)

    html = html.replace(/<hr\s*\/?>/g, '')

    html = html.replace(/---+/g, '')

    html = html.replace(/^\s*#+\s*.*$/gm, '')
    html = html.replace(/^\s*>\s*.*$/gm, '')
    html = html.replace(/^\s*[*_]{3,}\s*$/gm, '')

    html = html.replace(/([^\n]+)(?=\n|$)/g, (m, line) => {
      if (/^<\/?(h[1-6]|ul|ol|li|p|pre|code|blockquote|img|strong|em|a|b)/.test(line.trim())) {
        return line
      }
      return `<p>${line.trim()}</p>`
    })

    html = html.replace(/<p>\s*<\/p>/g, '')

    // Fonts render the → glyph tiny/inconsistently, so replace it with an inline
    // SVG arrow (lucide arrow-right) that scales with the text and inherits its
    // color in both themes.
    html = replaceArrows(html)

    return links.restore(html)
  }

  /**
   * Turn markdown-style list lines into real <ol>/<ul> markup. Numbered lines keep
   * the number the backend sent (via the li value attribute), so stepped answers
   * ("1. Go to… 2. Click…") render with their numbering instead of flat paragraphs.
   */
  const parseLists = (text: string): string => {
    const lines = text.split('\n')
    let result = ''
    let openList: 'ol' | 'ul' | null = null
    const closeList = () => {
      if (openList) {
        result += `</${openList}>`
        openList = null
      }
    }
    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed === '') continue
      const numbered = trimmed.match(/^(\d+)\.\s+(.*)$/)
      const bulleted = trimmed.match(/^[-+]\s+(.*)$/)
      if (numbered) {
        if (openList !== 'ol') {
          closeList()
          result += '<ol>'
          openList = 'ol'
        }
        result += `<li value="${numbered[1]}">${numbered[2]}</li>`
      } else if (bulleted) {
        if (openList !== 'ul') {
          closeList()
          result += '<ul>'
          openList = 'ul'
        }
        result += `<li>${bulleted[1]}</li>`
      } else {
        closeList()
        result += `<p>${trimmed}</p>`
      }
    }
    closeList()
    return result
  }

  const parseLLMResponse = (rawResponse: string): ParsedMessage => {
    const withoutCitations = removeCitations(rawResponse || '')
    const html = markdownToHtml(withoutCitations)
    return {html}
  }

  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const setImportantColor = (element: HTMLElement, color: string | null) => {
      try {
        if (color) {
          element.style.setProperty('color', color, 'important')
        } else {
          element.style.removeProperty('color')
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
    }

    const lightTextColor = '#212529'
    const lightMutedColor = '#6c757d'
    const narrativeSelector = 'h1,h2,h3,h4,h5,h6,p,li,span,a,code,pre,blockquote,img,hr'

    if (theme === 'dark') {
      setImportantColor(el, '#ffffff')
      el.querySelectorAll<HTMLElement>(narrativeSelector).forEach((n) =>
        setImportantColor(n, '#ffffff')
      )
      el.querySelectorAll<HTMLElement>('small').forEach((n) => setImportantColor(n, '#ffffff'))
    } else {
      setImportantColor(el, lightTextColor)
      el.querySelectorAll<HTMLElement>(narrativeSelector).forEach((n) =>
        setImportantColor(n, lightTextColor)
      )
      el.querySelectorAll<HTMLElement>('a').forEach((n) => setImportantColor(n, '#0d6efd'))
      el.querySelectorAll<HTMLElement>('small').forEach((n) =>
        setImportantColor(n, lightMutedColor)
      )
    }

    return () => {
      try {
        const selector = 'h1,h2,h3,h4,h5,h6,p,li,span,a,code,pre,small,blockquote,img,hr'
        el.querySelectorAll<HTMLElement>(selector).forEach((n) => {
          try {
            n.style.removeProperty('color')
          } catch (e) {
            calculateErrorMessage(e)
          }
        })
        try {
          el.style.removeProperty('color')
        } catch (e) {
          calculateErrorMessage(e)
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
    }
  }, [theme, message.text, message.content])

  const renderProseHtml = (text: string, ref?: React.RefObject<HTMLDivElement | null>) => {
    const htmlWithLinks = parseLLMResponse(String(text)).html

    return (
      <div
        ref={ref}
        data-kt-element='message-text'
        className='message-content bot-message-bubble'
        dangerouslySetInnerHTML={{__html: htmlWithLinks}}
      />
    )
  }

  /** A markdown pipe table (Versi fee lists), drawn as the same card as the attendance results. */
  const renderMarkdownTable = (
    table: MarkdownTableSegment,
    key: number,
    narrative: string,
    footer: string
  ) => (
    <MarkdownTableCard
      key={key}
      columns={table.columns}
      rows={table.rows}
      narrative={narrative}
      footer={footer}
      capability={message.capability}
      theme={theme}
    />
  )

  const renderHtmlBody = (text: string, ref?: React.RefObject<HTMLDivElement | null>) => {
    if (!text?.trim()) return null
    const profile = DIRECTORY_PROFILE_CARD ? parseDirectoryProfile(String(text)) : null
    if (profile) {
      return (
        <>
          <DirectoryProfileCard profile={profile} theme={theme} />
          {profile.footer ? renderProseHtml(profile.footer) : null}
        </>
      )
    }
    const segments = splitMarkdownTables(String(text))
    if (!segments.some((segment) => segment.kind === 'table')) return renderProseHtml(text, ref)

    // The colour fix-up ref goes on the first text part (the lead sentence).
    const firstTextIndex = segments.findIndex((segment) => segment.kind === 'text')
    // A table's export carries the text just above it (the lead sentence) and just below it.
    const textAt = (index: number) => {
      const segment = segments[index]
      return segment?.kind === 'text' ? segment.text : ''
    }
    return (
      <>
        {segments.map((segment, i) =>
          segment.kind === 'table' ? (
            renderMarkdownTable(segment, i, textAt(i - 1), textAt(i + 1))
          ) : (
            <React.Fragment key={i}>
              {renderProseHtml(segment.text, i === firstTextIndex ? ref : undefined)}
            </React.Fragment>
          )
        )}
      </>
    )
  }

  const renderMessageBody = (text: string) => (
    <AttendanceMessageBody
      text={text}
      v2Payload={message.v2Payload}
      rawApiAnswer={message.rawApiAnswer}
      attendanceContext={message.attendanceContext}
      presentation={message.presentation}
      theme={theme}
      renderHtmlBody={renderHtmlBody}
      contentRef={contentRef}
      onFollowUp={onOptionSelect}
    />
  )

  const timestamp = message.timestamp ? new Date(message.timestamp as any) : new Date()

  if (Array.isArray(message.text)) {
    return (
      <div className='chatbot-message-row chatbot-message-row--bot'>
        <div className='chatbot-bot-avatar chatbot-bot-avatar--mascot' aria-hidden='true'>
          <AttendanceBotAvatar />
        </div>
        <div className='chatbot-bot-bubble'>
          <ol className='mb-0 ps-3'>
            {message.text.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        </div>
      </div>
    )
  }

  return (
    <div className='chatbot-message-row chatbot-message-row--bot'>
      <div className='chatbot-bot-avatar chatbot-bot-avatar--mascot' aria-hidden='true'>
        <AttendanceBotAvatar />
      </div>

      <div className='chatbot-bot-bubble'>
        {renderMessageBody(message.text)}
        {message.presentation === 'steps' && message.steps?.length ? (
          <AssistantStepsView steps={message.steps} />
        ) : null}
        {message.clarification && onClarificationSelect ? (
          <AssistantClarificationChips
            clarification={message.clarification}
            onSelect={onClarificationSelect}
            disabled={chipsDisabled}
          />
        ) : null}
        {message.suggestions?.length && onSuggestionSelect ? (
          <AssistantSuggestionChips
            suggestions={message.suggestions}
            onSelect={onSuggestionSelect}
            disabled={chipsDisabled}
          />
        ) : null}
        {message.id === 'welcome' &&
        welcomeSuggestions &&
        welcomeSuggestions.length > 0 &&
        onWelcomeSuggestionSelect ? (
          <AttendanceInputSuggestionPills
            suggestions={welcomeSuggestions}
            onSelect={onWelcomeSuggestionSelect}
            disabled={welcomeSuggestionsDisabled}
            theme={theme}
            embedded
          />
        ) : null}
        {message.optionChips && message.optionChips.length > 0 && onOptionSelect ? (
          <div className='mt-2'>
            <OptionChips
              options={message.optionChips}
              onSelect={onOptionSelect}
              theme={theme}
              layout='vertical'
            />
          </div>
        ) : null}
        {message.offerTicket && onCreateTicket ? (
          <div className='chatbot-bot-bubble__actions'>
            <button type='button' className='chatbot-faq-ticket-action' onClick={onCreateTicket}>
              <Ticket size={14} aria-hidden='true' />
              {intl.formatMessage({id: 'CHATBOT.FAQ.CREATE_TICKET'})}
            </button>
          </div>
        ) : null}
        <div className='chatbot-bot-bubble__meta'>
          {timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  )
}

export default BotMessage
